/**
 * Subscriptions Hook
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { subscriptionService } from '../services/subscriptionService';
import type { CreateSubscriptionDto, UpdateSubscriptionDto, UpdateModulePriceDto } from '../types';

const QUERY_KEYS = {
  modules: ['platform', 'modules'],
  subscriptions: ['platform', 'subscriptions'],
  subscription: (tenantId: string) => ['platform', 'subscriptions', tenantId],
  smsUsage: (tenantId: string, month?: string) => ['platform', 'sms-usage', tenantId, month],
  price: (tenantId: string) => ['platform', 'price', tenantId],
};

export function useModules() {
  return useQuery({
    queryKey: QUERY_KEYS.modules,
    queryFn: subscriptionService.getModules,
    staleTime: 5 * 60 * 1000, // 5 minutes - modules don't change often
  });
}

export function useSubscription(tenantId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.subscription(tenantId),
    queryFn: () => subscriptionService.getByTenantId(tenantId),
    enabled: !!tenantId,
  });
}

export function useAllSubscriptions() {
  return useQuery({
    queryKey: QUERY_KEYS.subscriptions,
    queryFn: subscriptionService.getAll,
  });
}

export function useCreateSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, dto }: { tenantId: string; dto: CreateSubscriptionDto }) =>
      subscriptionService.create(tenantId, dto),
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscription(tenantId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscriptions });
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
    },
  });
}

export function useUpdateSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      dto,
    }: {
      tenantId: string;
      dto: UpdateSubscriptionDto;
    }) => subscriptionService.update(tenantId, dto),
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscription(tenantId) });
      // Invalidate both the tenant list and specific tenant query
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants', tenantId] });
    },
  });
}

export function useActivateModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      moduleKey,
    }: {
      tenantId: string;
      moduleKey: string;
    }) => subscriptionService.activateModule(tenantId, moduleKey),
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscription(tenantId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.price(tenantId) });
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
    },
  });
}

export function useDeactivateModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      moduleKey,
    }: {
      tenantId: string;
      moduleKey: string;
    }) => subscriptionService.deactivateModule(tenantId, moduleKey),
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscription(tenantId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.price(tenantId) });
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
    },
  });
}

export function useSmsUsage(tenantId: string, month?: string) {
  return useQuery({
    queryKey: QUERY_KEYS.smsUsage(tenantId, month),
    queryFn: () => subscriptionService.getSmsUsage(tenantId, month),
    enabled: !!tenantId,
  });
}

export function usePriceCalculation(tenantId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.price(tenantId),
    queryFn: () => subscriptionService.calculatePrice(tenantId),
    enabled: !!tenantId,
  });
}

export function useUpdateModulePrice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      moduleKey,
      dto,
    }: {
      tenantId: string;
      moduleKey: string;
      dto: UpdateModulePriceDto;
    }) => subscriptionService.updateModulePrice(tenantId, moduleKey, dto),
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscription(tenantId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.price(tenantId) });
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      // Also invalidate the specific tenant query used by useTenant hook
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants', tenantId] });
    },
  });
}

export function useBillingReport(period?: string) {
  return useQuery({
    queryKey: ['platform', 'billing-report', period],
    queryFn: () => subscriptionService.getBillingReport(period),
  });
}

export function useUpdateModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      moduleKey,
      dto,
    }: {
      moduleKey: string;
      dto: { basePrice?: number; isActive?: boolean };
    }) => subscriptionService.updateModule(moduleKey, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.modules });
      queryClient.invalidateQueries({ queryKey: ['platform', 'billing-report'] });
    },
  });
}
